import {StateController} from "@lit-app/state/src/state-controller";
import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {state} from "lit/decorators/state.js";
import {ifDefined} from "lit/directives/if-defined.js";
import {translate} from "lit-translate";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, filter, map, takeUntil} from "rxjs/operators";

import {appState} from "../../app.state";
import {KnowledgeFiltersDTO} from "../../domain/dtos/knowledge-filters.dto";
import {ComboboxItem} from "../_inputs/ha-combobox/combobox-item.interface";
import {AbstractLitElement} from "../abstract-lit.element";

// Allowed values for the KnowledgeObject `type` filter dropdown.
// Extend by adding new entries here as new KnowledgeObject types ship.
const KNOWLEDGE_TYPE_ITEMS: ReadonlyArray<ComboboxItem> = [
    {id: 'whiteboard', title: 'Whiteboard', payload: 'whiteboard'},
    {id: 'document', title: 'Textdokument', payload: 'document'},
];

@customElement(`ha-form-knowledge-filters`)
export class HaFormKnowledgeFiltersComponent extends AbstractLitElement {

    @state()
    private _selectedTypeItem: ComboboxItem | null = null;

    private _titleSearchSubject = new Subject<string>();

    constructor() {
        super();
        new StateController(this, appState);
    }

    public connectedCallback() {
        super.connectedCallback();

        // Sync local combobox state with whatever is currently in appState.
        this._selectedTypeItem = this._resolveTypeItem(appState.knowledgeFilters?.type);

        // Debounce title search: fire after 400ms idle when empty or >= 3 chars.
        this._titleSearchSubject.pipe(
            map(value => value.trim()),
            filter(value => value.length === 0 || value.length >= 3),
            debounceTime(400),
            distinctUntilChanged(),
            takeUntil(this._disconnected$)
        ).subscribe(titleSearch => this._writeFilter('titleSearch', titleSearch || undefined));
    }

    protected render(): unknown {
        const filters = appState.knowledgeFilters ?? {};
        return html`
            <ha-combobox class="max9999-flexcolumn" data-type="type"
                         label=${translate('ha-sidebar-filter.knowledge.type')}
                         .items=${KNOWLEDGE_TYPE_ITEMS as ComboboxItem[]}
                         .value=${ifDefined(this._selectedTypeItem ?? undefined) as unknown as never}
                         @value-changed=${this._onTypeChanged}
                         with-clear-button
            ></ha-combobox>
            <ha-input-text class="max9999-flexcolumn"
                           label=${translate('ha-sidebar-filter.knowledge.title-search')}
                           .value=${filters.titleSearch ?? ''}
                           @value-changed=${this._onTitleSearchInput}
                           with-clear-button
            ></ha-input-text>
        `;
    }

    private _onTypeChanged = (evt: CustomEvent<{ value: ComboboxItem | null }>): void => {
        const item = evt.detail.value;
        this._selectedTypeItem = item;
        this._writeFilter('type', (item?.payload as string | undefined) ?? undefined);
    };

    private _onTitleSearchInput = (evt: CustomEvent<{ value: string }>): void => {
        this._titleSearchSubject.next(evt.detail.value ?? '');
    };

    /** Maps a stored type string back to the ComboboxItem the combobox renders. */
    private _resolveTypeItem(type: string | undefined): ComboboxItem | null {
        if (!type) {
            return null;
        }
        return KNOWLEDGE_TYPE_ITEMS.find(item => item.payload === type) ?? null;
    }

    /**
     * Writes a single filter key to `appState.knowledgeFilters`; removes the
     * key when value is undefined/empty. Dispatches `knowledge-filters-changed`
     * so the parent view reloads its list.
     */
    private _writeFilter<K extends keyof KnowledgeFiltersDTO>(key: K, value: KnowledgeFiltersDTO[K] | undefined): void {
        const next: KnowledgeFiltersDTO = {...(appState.knowledgeFilters ?? {})};
        if (value === undefined || value === null || value === '') {
            delete next[key];
        } else {
            next[key] = value;
        }
        appState.knowledgeFilters = next;
        this.dispatchEvent(new CustomEvent('knowledge-filters-changed', {bubbles: true, composed: false}));
    }
}
